import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * NumberTiles — "Count to Ten" hook for the numbers lesson.
 *
 * Ten tiles, one per number (وَاحِد…عَشَرَة), each showing its Arabic-Indic
 * digit. Tiles must be tapped in counting order — only the next number pulses;
 * tapping it reveals the word, transliteration and meaning, and plays the
 * word. Completes when the student has counted all the way to ten.
 */
export default function NumberTiles({ data, onComplete, audio }) {
  const [counted, setCounted] = useState(0);

  const items = data.items;
  const done = counted >= items.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handleTap = (idx) => {
    if (idx !== counted) return;
    setCounted(idx + 1);
    if (audio) audio.play(items[idx].arabic);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{counted}/{items.length} counted</ProgressPill>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {items.map((item, idx) => {
          const revealed = idx < counted;
          const isNext = idx === counted;
          // A revealed tile must not be a (disabled) button — that would
          // swallow clicks on the nested PlayButton (same as WordCards).
          if (revealed) {
            return (
              <div
                key={item.id}
                className="flex flex-col items-center gap-1 rounded-xl px-3 py-4"
                style={{ background: "var(--ink)", border: "1px solid var(--gold-dim)" }}
              >
                <span dir="rtl" lang="ar" className="font-arabic text-4xl leading-[1.4]" style={{ color: "var(--gold-bright)" }}>
                  {item.digit}
                </span>
                <span dir="rtl" lang="ar" className="font-arabic text-2xl leading-[1.6]" style={{ color: "var(--parchment)" }}>
                  {item.arabic}
                </span>
                <span className="text-xs italic" style={{ color: "var(--gold-dim)" }}>{item.translit}</span>
                <span className="text-xs inline-flex items-center gap-1.5" style={{ color: "var(--parchment-dim)" }}>
                  {item.en}
                  {audio && audio.audioAllowed && (
                    <PlayButton text={item.arabic} playingText={audio.playingText} onPlay={audio.play} />
                  )}
                </span>
              </div>
            );
          }
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleTap(idx)}
              disabled={!isNext}
              className={`flex flex-col items-center gap-1 rounded-xl px-3 py-4 transition-all ${
                isNext ? "hover:brightness-125 animate-pulse cursor-pointer" : "cursor-not-allowed opacity-50"
              }`}
              style={
                isNext
                  ? { background: "var(--gold-bg)", border: "1px dashed var(--gold-dim)" }
                  : { background: "var(--ink)", border: "1px solid var(--panel-line)" }
              }
              title={isNext ? "Tap to count this number" : undefined}
            >
              <span
                dir="rtl"
                lang="ar"
                className="font-arabic text-4xl leading-[1.4]"
                style={{ color: isNext ? "var(--gold-bright)" : "var(--parchment-dim)" }}
              >
                {item.digit}
              </span>
              <span className="text-lg" aria-hidden="true" style={{ color: "var(--parchment-dim)", opacity: 0.5 }}>
                ?
              </span>
            </button>
          );
        })}
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
